/**
 * GiftSetService – reads the promo code captured by seatcoversolutions.com's
 * assets/scs-gift-code.js (either the live ?promoCode= param, or its 30-day
 * scs_gift_code cookie fallback once the visitor has navigated away from the
 * landing page) and, when one is active, fetches from scsbiz-preorder's
 * public gift-set lookup:
 *  - which variant(s)/quantities are free for that code (used by StepNav to
 *    add the promo's gifts to cart instead of the theme section's
 *    campaign-flag-driven bonus items, window.scs_bonus_product etc), and
 *  - an optional bundleDisplay override for SuperOffer's .bf_wrapper box
 *    (used in place of the theme's window.scs_promo_bonus_* settings when
 *    the merchant has configured one for this promo in the Gift Sets admin).
 */

const GIFT_SET_API_BASE = "https://scsbiz-preorder.wearevisum.com";
const GIFT_CODE_COOKIE = "scs_gift_code";

export interface GiftSetVariant {
  id: string;
  freeQuantity: number;
}

export interface BundleDisplayItem {
  title?: string;
  imageUrl?: string;
  alt?: string;
  price?: string;
  desc?: string;
}

export interface BundleDisplayWarranty {
  enabled?: boolean;
  icon?: string;
  iconImageUrl?: string;
  text?: string;
  msrp?: string;
  badge?: string;
}

export interface BundleDisplay {
  heading1?: string;
  heading2?: string;
  items?: BundleDisplayItem[];
  warranty?: BundleDisplayWarranty;
}

interface GiftSetData {
  variants: GiftSetVariant[];
  bundleDisplay: BundleDisplay | null;
}

const EMPTY_DATA: GiftSetData = { variants: [], bundleDisplay: null };

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

export function getActivePromoCode(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get("promoCode") || getCookie(GIFT_CODE_COOKIE);
}

let cache: { code: string; data: GiftSetData } | null = null;
let inflight: { code: string; promise: Promise<GiftSetData> } | null = null;

async function fetchGiftSetData(promoCode: string): Promise<GiftSetData> {
  if (cache?.code === promoCode) return cache.data;
  if (inflight?.code === promoCode) return inflight.promise;

  const promise = (async (): Promise<GiftSetData> => {
    try {
      const url = new URL(`${GIFT_SET_API_BASE}/public/gift-set`);
      url.searchParams.set("promoCode", promoCode);
      const response = await fetch(url.toString(), { mode: "cors" });
      if (!response.ok) throw new Error(`Failed to fetch gift set: ${response.statusText}`);
      const json = await response.json();
      const data: GiftSetData = {
        variants: Array.isArray(json?.variants) ? json.variants : [],
        bundleDisplay: json?.bundleDisplay ?? null,
      };
      cache = { code: promoCode, data };
      return data;
    } catch (err) {
      console.warn("[GiftSetService] Failed to fetch gift-set data:", err);
      return EMPTY_DATA;
    } finally {
      inflight = null;
    }
  })();
  inflight = { code: promoCode, promise };
  return promise;
}

/** Resolves to [] (never throws) when the code isn't found or the request fails. */
export async function fetchGiftSetVariants(promoCode: string): Promise<GiftSetVariant[]> {
  return (await fetchGiftSetData(promoCode)).variants;
}

/** Resolves to null (never throws) when the code isn't found, has no bundle configured, or the request fails. */
export async function fetchGiftSetBundleDisplay(promoCode: string): Promise<BundleDisplay | null> {
  return (await fetchGiftSetData(promoCode)).bundleDisplay;
}

export function numericVariantId(gid: string): string {
  return gid.replace("gid://shopify/ProductVariant/", "");
}

/** @internal Test-only reset */
export function _resetForTest(): void {
  cache = null;
  inflight = null;
}
